import React from 'react';
import styled from 'styled-components';
import { adminTheme } from '../../theme-admin';

const Wrap = styled.div`
  background: ${adminTheme.colors.background.primary};
  border: 1px solid ${adminTheme.colors.gray[200]};
  border-radius: ${adminTheme.borderRadius.xl};
  overflow: hidden;
  box-shadow: ${adminTheme.shadows.sm};
`;

const Head = styled.div`
  display: grid;
  grid-template-columns: ${(p)=>p.$cols};
  padding: ${adminTheme.spacing.lg} ${adminTheme.spacing.lg};
  background: linear-gradient(135deg, ${adminTheme.colors.gray[50]} 0%, ${adminTheme.colors.gray[100]} 100%);
  font-family: ${adminTheme.fonts.heading};
  font-weight: ${adminTheme.fontWeights.bold};
  font-size: ${adminTheme.fontSizes.sm};
  color: ${adminTheme.colors.gray[700]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid ${adminTheme.colors.gray[200]};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: ${(p)=>p.$cols};
  padding: ${adminTheme.spacing.md} ${adminTheme.spacing.lg};
  border-bottom: 1px solid ${adminTheme.colors.gray[100]};
  align-items: center;
  transition: ${adminTheme.transitions.fast};
  background: ${adminTheme.colors.background.primary};

  &:hover {
    background: ${adminTheme.colors.gray[50]};
    transform: translateX(2px);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Cell = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${adminTheme.colors.gray[900]};
  font-size: ${adminTheme.fontSizes.sm};
  font-weight: ${adminTheme.fontWeights.medium};
`;

const EmptyState = styled.div`
  padding: ${adminTheme.spacing['3xl']} ${adminTheme.spacing.xl};
  text-align: center;
  color: ${adminTheme.colors.gray[500]};
  
  div:first-child {
    font-size: ${adminTheme.fontSizes['4xl']};
    margin-bottom: ${adminTheme.spacing.md};
  }
  
  div:last-child {
    font-size: ${adminTheme.fontSizes.base};
    font-weight: ${adminTheme.fontWeights.medium};
  }
`;

export default function DataTable({ 
  columns = [], 
  rows = [], 
  renderCell, 
  actions, 
  actionsHeader = 'Actions',
  emptyMessage = 'No data available'
}) {
  const template = [
    ...columns.map(c => c.width || '1fr'),
    ...(actions ? ['auto'] : []),
  ].join(' ');

  return (
    <Wrap>
      <Head $cols={template}>
        {columns.map((c) => (
          <Cell key={c.key}>{c.header ?? c.label}</Cell>
        ))}
        {actions && (
          <Cell style={{ textAlign: 'right', color: adminTheme.colors.gray[600] }}>
            {actionsHeader}
          </Cell>
        )}
      </Head>
      {rows.length === 0 ? (
        <EmptyState>
          <div>📭</div>
          <div>{emptyMessage}</div>
        </EmptyState>
      ) : (
        rows.map((r, idx) => (
          <Row key={r._id || r.id || idx} $cols={template}>
            {columns.map((c) => (
              <Cell key={c.key}>
                {typeof c.render === 'function'
                  ? c.render(r)
                  : renderCell
                  ? renderCell(c, r)
                  : (r[c.key] ?? '')}
              </Cell>
            ))}
            {actions && (
              <Cell style={{ 
                display: 'flex', 
                gap: adminTheme.spacing.sm, 
                justifyContent: 'flex-end' 
              }}>
                {actions(r)}
              </Cell>
            )}
          </Row>
        ))
      )}
    </Wrap>
  );
}
